// ═══════════════════════════════════════════════════════════════
// checklistAutoChecks — runtime auto-check computation
// Computes AutoCheckResult[] for each invoice type.
// These only cover items with evidenceType === 'auto'.
// ═══════════════════════════════════════════════════════════════

import type { AutoCheckResult } from '../types/checklist';
import type { DebrisInvoice, DebrisLoadTicket, DebrisTruck } from '../types/debris';
import { FEMA_EQUIP_RATES } from '../data/femaEquipmentRates';

// ── Debris auto-checks ───────────────────────────────────────

export function computeDebrisAutoChecks(
  invoice: DebrisInvoice,
  tickets: DebrisLoadTicket[],
  trucks: DebrisTruck[],
): AutoCheckResult[] {
  const results: AutoCheckResult[] = [];

  // Derive linked tickets for downstream checks.
  // `tickets` may already be period-filtered by the caller as a fallback for PackageProcessor imports.
  let ticketIds: number[] = [];
  try { ticketIds = JSON.parse(invoice.ticketIds || '[]'); } catch { ticketIds = []; }
  const linkedTickets = tickets; // caller pre-filters: by ticketIds OR by contractor+period

  // deb-001: Haul Tickets on File
  results.push({
    templateItemId: 'deb-001',
    label: 'Haul Tickets on File',
    status: linkedTickets.length > 0 ? 'pass' : 'fail',
    detail: linkedTickets.length > 0
      ? `${linkedTickets.length} ticket${linkedTickets.length !== 1 ? 's' : ''} ${ticketIds.length > 0 ? 'formally linked' : 'found for this contractor/period'}`
      : 'No tickets found — import via PackageProcessor or run Calculate to link',
  });

  // deb-002: CY Reconciliation
  const invoicedCY = parseFloat(invoice.totalCubicYards || '0');
  const ticketCY = linkedTickets.reduce((sum, t) => {
    const stored = parseFloat(t.calculatedCubicYards || '0');
    if (stored > 0) return sum + stored;
    const loadPct = parseFloat(t.loadPercentage || '0');
    const truck = trucks.find(tr => String(tr.id) === t.truckId);
    const cap = truck ? parseFloat(truck.cubicYardCapacity || '0') : 0;
    return sum + (cap > 0 ? cap * (loadPct / 100) : 0);
  }, 0);
  if (ticketCY > 0) {
    const variance = Math.abs((invoicedCY - ticketCY) / ticketCY);
    const pct = (variance * 100).toFixed(1);
    results.push({
      templateItemId: 'deb-002',
      label: 'CY Quantity Reconciliation',
      status: variance <= 0.02 ? 'pass' : 'fail',
      detail: `Invoice: ${invoicedCY.toFixed(1)} CY, Tickets: ${ticketCY.toFixed(1)} CY, variance ${pct}%`,
    });
  } else {
    results.push({
      templateItemId: 'deb-002',
      label: 'CY Quantity Reconciliation',
      status: 'unavailable',
      detail: 'No ticket CY data available for comparison',
    });
  }

  // deb-003: Rate Compliance
  const ticketsWithRate = linkedTickets.filter(t =>
    parseFloat(t.extendedCost || '0') > 0 &&
    parseFloat(t.transactionRate || '0') > 0 &&
    parseFloat(t.calculatedCubicYards || '0') > 0,
  );
  if (ticketsWithRate.length > 0) {
    const mismatches: string[] = [];
    for (const t of ticketsWithRate) {
      const cy = parseFloat(t.calculatedCubicYards);
      const rate = parseFloat(t.transactionRate!);
      const extended = parseFloat(t.extendedCost!);
      const expected = cy * rate;
      if (expected > 0 && Math.abs((extended - expected) / expected) > 0.02) {
        mismatches.push(t.ticketNumber || String(t.id));
      }
    }
    results.push({
      templateItemId: 'deb-003',
      label: 'Rate Compliance',
      status: mismatches.length === 0 ? 'pass' : 'fail',
      detail: mismatches.length === 0
        ? `All ${ticketsWithRate.length} rated tickets pass 2% tolerance`
        : `${mismatches.length} ticket${mismatches.length !== 1 ? 's' : ''} exceed ±2% rate tolerance`,
    });
  } else {
    results.push({
      templateItemId: 'deb-003',
      label: 'Rate Compliance',
      status: 'unavailable',
      detail: 'Tickets lack extended cost / rate data (requires TDMS import)',
    });
  }

  // deb-004: Truck Certifications
  const truckNumbers = new Set(linkedTickets.map(t => t.truckNumber || t.truckId).filter(Boolean));
  if (truckNumbers.size > 0) {
    const uncertified: string[] = [];
    for (const num of truckNumbers) {
      const truck = trucks.find(tr => tr.truckNumber === num || String(tr.id) === num);
      if (!truck) continue;
      const isActive = truck.certStatus === 'certified' || truck.certStatus === 'active' || truck.status === 'certified';
      // Check cert expiry against earliest work date on this invoice
      const workDates = linkedTickets
        .filter(t => t.truckNumber === num || t.truckId === num)
        .map(t => t.pickupTime || t.disposalTime || '')
        .filter(Boolean)
        .sort();
      const earliestWork = workDates[0] ?? '';
      const expiry = truck.certExpiresAt || '';
      const expired = expiry && earliestWork && expiry < earliestWork.slice(0, 10);
      if (!isActive || expired) {
        uncertified.push(truck.truckNumber || String(truck.id));
      }
    }
    results.push({
      templateItemId: 'deb-004',
      label: 'Truck Certifications on File',
      status: uncertified.length === 0 ? 'pass' : 'fail',
      detail: uncertified.length === 0
        ? `${truckNumbers.size} truck${truckNumbers.size !== 1 ? 's' : ''} certified`
        : `Uncertified or expired: ${uncertified.slice(0, 5).join(', ')}${uncertified.length > 5 ? ` +${uncertified.length - 5} more` : ''}`,
    });
  } else {
    results.push({
      templateItemId: 'deb-004',
      label: 'Truck Certifications on File',
      status: 'unavailable',
      detail: 'No truck data on linked tickets',
    });
  }

  // deb-005: Invoice Document on File
  // |||DOCID||| = manual upload; |||TAMPA||| = PackageProcessor processed the invoice PDF
  const notesText = (invoice.notes ?? '') + ((invoice as any).reviewerNotes ?? '');
  const hasDoc = /\|\|\|DOCID\|\|\|/.test(notesText) || /\|\|\|TAMPA\|\|\|/.test(notesText);
  results.push({
    templateItemId: 'deb-005',
    label: 'Invoice Document on File',
    status: hasDoc ? 'pass' : 'fail',
    detail: hasDoc ? 'Document on file' : 'No invoice document uploaded',
  });

  return results;
}

// ── Non-Debris Contractor auto-checks ───────────────────────

export function computeNonDebrisAutoChecks(
  invoice: DebrisInvoice,
  checklistItems?: Array<{ templateItemId: string; status: string; fileDocId?: string }>,
): AutoCheckResult[] {
  const results: AutoCheckResult[] = [];
  const notesText = (invoice.notes ?? '') + ((invoice as any).reviewerNotes ?? '');
  const hasDoc = /\|\|\|DOCID\|\|\|/.test(notesText) || /\|\|\|TAMPA\|\|\|/.test(notesText);

  // ndc-001: Invoice Document on File
  results.push({
    templateItemId: 'ndc-001',
    label: 'Invoice Document on File',
    status: hasDoc ? 'pass' : 'fail',
    detail: hasDoc ? 'Document on file' : 'No invoice document uploaded',
  });

  // ndc-003: SOW / Work Order Linked
  const sowItem = checklistItems?.find(ci => ci.templateItemId === 'ndc-003');
  const sowLinked = sowItem && sowItem.status === 'satisfied' && !!sowItem.fileDocId;
  results.push({
    templateItemId: 'ndc-003',
    label: 'SOW / Work Order Linked',
    status: sowLinked ? 'pass' : 'fail',
    detail: sowLinked ? 'SOW/Work Order document linked' : 'No SOW document linked yet',
  });

  // ndc-004: Inspection / Completion Sign-Off Linked
  const inspItem = checklistItems?.find(ci => ci.templateItemId === 'ndc-004');
  const inspLinked = inspItem && inspItem.status === 'satisfied' && !!inspItem.fileDocId;
  results.push({
    templateItemId: 'ndc-004',
    label: 'Inspection / Completion Sign-Off Linked',
    status: inspLinked ? 'pass' : 'fail',
    detail: inspLinked ? 'Inspection/completion sign-off linked' : 'No inspection sign-off linked yet',
  });

  return results;
}

// ── Force Account Labor auto-checks ─────────────────────────

interface LaborEntry {
  id: number;
  dateWorked?: string;
  classification?: string;
  notes?: string;
  [key: string]: unknown;
}

export function computeFALaborAutoChecks(
  claim: { billingPeriodStart: string; billingPeriodEnd: string },
  laborEntries: LaborEntry[],
  faDocMap: Record<string, string>,
): AutoCheckResult[] {
  const results: AutoCheckResult[] = [];
  const start = claim.billingPeriodStart?.slice(0, 10) ?? '';
  const end = claim.billingPeriodEnd?.slice(0, 10) ?? '';

  // fal-001: Labor Entries Recorded
  const inPeriod = laborEntries.filter(e => {
    const d = (e.dateWorked ?? '').slice(0, 10);
    return d && (!start || d >= start) && (!end || d <= end);
  });
  results.push({
    templateItemId: 'fal-001',
    label: 'Labor Entries Recorded',
    status: inPeriod.length > 0 ? 'pass' : 'fail',
    detail: inPeriod.length > 0
      ? `${inPeriod.length} labor entr${inPeriod.length !== 1 ? 'ies' : 'y'} in billing period`
      : 'No labor entries found for this billing period',
  });

  // fal-002: Employee Classifications
  const missingClass = inPeriod.filter(e => !e.classification || String(e.classification).trim() === '');
  results.push({
    templateItemId: 'fal-002',
    label: 'Employee Classifications Documented',
    status: missingClass.length === 0 ? (inPeriod.length > 0 ? 'pass' : 'unavailable') : 'fail',
    detail: inPeriod.length === 0
      ? 'No entries to check'
      : missingClass.length === 0
        ? `All ${inPeriod.length} entries have classifications`
        : `${missingClass.length} entr${missingClass.length !== 1 ? 'ies' : 'y'} missing classification`,
  });

  // fal-003: FEMA Form Documentation
  const docsCount = Object.keys(faDocMap).length;
  results.push({
    templateItemId: 'fal-003',
    label: 'FEMA Form Documentation (ANNEX E-3)',
    status: docsCount > 0 ? 'pass' : 'fail',
    detail: docsCount > 0
      ? `${docsCount} supporting document${docsCount !== 1 ? 's' : ''} attached across ${inPeriod.length} entr${inPeriod.length !== 1 ? 'ies' : 'y'}`
      : 'No FEMA form documents attached',
  });

  return results;
}

// ── Force Account Equipment auto-checks ─────────────────────

interface EquipEntry {
  id: number;
  dateUsed?: string;
  hourlyRate?: string | number;
  description?: string;
  operatorName?: string;
  [key: string]: unknown;
}

export function computeFAEquipAutoChecks(
  claim: { billingPeriodStart: string; billingPeriodEnd: string },
  equipEntries: EquipEntry[],
  faDocMap: Record<string, string>,
  _femaRates?: unknown,
): AutoCheckResult[] {
  const results: AutoCheckResult[] = [];
  const start = claim.billingPeriodStart?.slice(0, 10) ?? '';
  const end = claim.billingPeriodEnd?.slice(0, 10) ?? '';

  const inPeriod = equipEntries.filter(e => {
    const d = (e.dateUsed ?? '').slice(0, 10);
    return d && (!start || d >= start) && (!end || d <= end);
  });

  // fae-001: Equipment Entries Recorded
  results.push({
    templateItemId: 'fae-001',
    label: 'Equipment Entries Recorded',
    status: inPeriod.length > 0 ? 'pass' : 'fail',
    detail: inPeriod.length > 0
      ? `${inPeriod.length} equipment entr${inPeriod.length !== 1 ? 'ies' : 'y'} in billing period`
      : 'No equipment entries for this billing period',
  });

  // fae-002: FEMA Rate Schedule Applied
  const femaRateValues = FEMA_EQUIP_RATES.map(r => r.rate);
  if (inPeriod.length > 0) {
    const unrecognized: number[] = [];
    for (const e of inPeriod) {
      const entryRate = typeof e.hourlyRate === 'string' ? parseFloat(e.hourlyRate) : (e.hourlyRate ?? 0);
      if (!entryRate || entryRate <= 0) { unrecognized.push(e.id); continue; }
      const match = femaRateValues.some(r => Math.abs((entryRate - r) / r) <= 0.01);
      if (!match) unrecognized.push(e.id);
    }
    results.push({
      templateItemId: 'fae-002',
      label: 'FEMA Rate Schedule Applied',
      status: unrecognized.length === 0 ? 'pass' : 'fail',
      detail: unrecognized.length === 0
        ? `All ${inPeriod.length} entries use recognized FEMA rates`
        : `${unrecognized.length} entr${unrecognized.length !== 1 ? 'ies' : 'y'} with unrecognized hourly rates`,
    });
  } else {
    results.push({
      templateItemId: 'fae-002',
      label: 'FEMA Rate Schedule Applied',
      status: 'unavailable',
      detail: 'No entries in period',
    });
  }

  // fae-003: Equipment Descriptions Sufficient
  if (inPeriod.length > 0) {
    const shortDesc = inPeriod.filter(e => !e.description || String(e.description).trim().length < 6);
    results.push({
      templateItemId: 'fae-003',
      label: 'Equipment Descriptions Sufficient',
      status: shortDesc.length === 0 ? 'pass' : 'fail',
      detail: shortDesc.length === 0
        ? `All ${inPeriod.length} entries have sufficient descriptions`
        : `${shortDesc.length} entr${shortDesc.length !== 1 ? 'ies' : 'y'} with insufficient description (< 6 chars)`,
    });
  } else {
    results.push({
      templateItemId: 'fae-003',
      label: 'Equipment Descriptions Sufficient',
      status: 'unavailable',
      detail: 'No entries in period',
    });
  }

  // fae-004: Operators Named
  if (inPeriod.length > 0) {
    const missingOp = inPeriod.filter(e => !e.operatorName || String(e.operatorName).trim() === '');
    results.push({
      templateItemId: 'fae-004',
      label: 'Operator Names Documented',
      status: missingOp.length === 0 ? 'pass' : 'fail',
      detail: missingOp.length === 0
        ? `All ${inPeriod.length} entries have operator names`
        : `${missingOp.length} entr${missingOp.length !== 1 ? 'ies' : 'y'} missing operator name`,
    });
  } else {
    results.push({
      templateItemId: 'fae-004',
      label: 'Operator Names Documented',
      status: 'unavailable',
      detail: 'No entries in period',
    });
  }

  void faDocMap; // faDocMap available for future FEMA form checks

  return results;
}
